import React from 'react';
import { Typography, Box, Grid, CircularProgress } from '@mui/material';

type ImprovementEvaluationSummaryDisplayProps = {
  totalLowCost: number;
  totalHighCost: number;
  renovationValue: number;
  score: number; // Score from 1 to 100
  address: string;
  currentPrice: number; // Current listing price
  scoreDetails: string; // Additional details about the score
  valueAddRange: { minValue: number; maxValue: number }; // Range for standalone value add
};

// Calculate letter grade based on score
const getLetterGrade = (score: number) => {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 50) return 'C';
  if (score >= 30) return 'D';
  return 'F';
};

const getScoreColor = (score: number) => {
  if (score > 75) return '#4CAF50'; // High score - Green
  if (score > 50) return '#FFA500'; // Moderate score - Orange
  return '#FF0000'; // Low score - Red
};

const ImprovementEvaluationSummaryDisplay: React.FC<ImprovementEvaluationSummaryDisplayProps> = ({
  totalLowCost,
  totalHighCost,
  renovationValue,
  score,
  address,
  currentPrice,
  scoreDetails,
  valueAddRange,
}) => {
  const grade = getLetterGrade(score);

  return (
    <Box sx={{ mt: 4, width: '100%' }}>
      <Grid container spacing={2} alignItems="center">
        {/* Values Section */}
        <Grid item xs={6} sx={{ ml: 2 }}>
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'bold' }}>
            Projected Costs
          </Typography>
          <Typography variant="body1" color="primary" sx={{ fontWeight: 'bold' }}>
            ${totalLowCost.toLocaleString()} - ${totalHighCost.toLocaleString()}
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ fontWeight: 'bold', mt: 2 }}>
            Projected Value
          </Typography>
          <Typography variant="body1" color="green" sx={{ fontWeight: 'bold' }}>
            ${valueAddRange.minValue.toLocaleString()} - ${valueAddRange.maxValue.toLocaleString()}
          </Typography>
        </Grid>

        {/* Score Section */}
        <Grid item xs={4} sx={{ textAlign: 'right' }}>
          <Box sx={{ position: 'relative', display: 'inline-flex', mt: 1 }}>
            <CircularProgress
              variant="determinate"
              value={score}
              size={70}
              thickness={5}
              sx={{
                color: getScoreColor(score),
                transform: 'rotate(-90deg)',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              <Typography variant="h6" color="textSecondary">
                {grade}
              </Typography>
            </Box>
          </Box>
          {/* Property Score Label */}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            Property Score
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImprovementEvaluationSummaryDisplay;
